/**
 * Lot Modal Controller
 *
 * Manages the lot form modal for creating and editing lots
 * @module portal/modules/ui/modals/lotModal
 */

import { els } from '../elements';
import { captureFocus, restoreFocus } from './focusManager';

const bodyEl = document.body;
let formMode: 'create' | 'edit' = 'create';

/**
 * Set the lot form mode (create or edit)
 */
export function setFormMode(mode: 'create' | 'edit'): void {
  formMode = mode === 'edit' ? 'edit' : 'create';
  if (els.lotFormTitle) {
    els.lotFormTitle.textContent = formMode === 'edit' ? 'Edit lot' : 'Add lot';
  }
}

/**
 * Check if the lot modal is currently open
 */
export function isModalOpen(): boolean {
  return !!(els.modal && !els.modal.hidden);
}

/**
 * Open the lot form modal
 * @param options - Configuration options
 */
export function openLotModal(options?: { mode?: 'create' | 'edit'; focusField?: boolean }): void {
  const desiredMode = options?.mode || formMode;
  setFormMode(desiredMode);

  captureFocus('lastActiveBeforeModal');

  if (els.modalBackdrop) {
    els.modalBackdrop.hidden = false;
  }
  if (els.modal) {
    els.modal.hidden = false;
  }
  if (bodyEl) {
    bodyEl.classList.add('modal-open');
  }

  const shouldFocus = !options || options.focusField !== false;
  if (shouldFocus && els.f && els.f.url) {
    window.requestAnimationFrame(() => {
      els.f.url.focus();
    });
  }
}

/**
 * Close the lot form modal
 * @param options - Configuration options
 */
export function closeLotModal(options?: { reset?: boolean; focusTarget?: HTMLElement }): void {
  if (els.modal) {
    els.modal.hidden = true;
  }
  if (els.modalBackdrop) {
    els.modalBackdrop.hidden = true;
  }
  if (bodyEl) {
    bodyEl.classList.remove('modal-open');
  }

  if (options?.reset) {
    if (els.form) {
      (els.form as HTMLFormElement).reset();
    }
    if (els.f?.id) {
      els.f.id.value = '';
    }
    setFormMode('create');
  }

  const fallback = options?.focusTarget || els.openLotForm;
  restoreFocus('lastActiveBeforeModal', fallback);
}
